import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, Copy, ChevronRight, Key, Upload, Webhook,
  Bot, Mail, Shield, Code2, FileSpreadsheet, CheckCircle,
  AlertTriangle, Settings, Globe
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsManualSetup() {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const authHeaderExample = `curl -X GET "https://api.commerceindex.com/v1/products" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "X-CI-Brand-Id: YOUR_BRAND_ID"`;

  const productCreateExample = `curl -X POST "https://api.commerceindex.com/v1/products" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "X-CI-Brand-Id: YOUR_BRAND_ID" \\
  -d '{
    "title": "Premium Wireless Headphones",
    "description": "High-fidelity Bluetooth headphones with ANC",
    "vendor": "AudioTech",
    "product_type": "Electronics",
    "tags": ["wireless", "bluetooth", "noise-cancelling"],
    "variants": [
      {
        "title": "Matte Black",
        "sku": "ATH-WH-001-BLK",
        "price": 89.99,
        "compare_at_price": 119.99,
        "inventory_quantity": 142,
        "barcode": "123456789012"
      },
      {
        "title": "Silver",
        "sku": "ATH-WH-001-SLV",
        "price": 89.99,
        "compare_at_price": 119.99,
        "inventory_quantity": 87,
        "barcode": "123456789013"
      }
    ],
    "images": [
      { "src": "https://example.com/images/headphones-black.jpg", "alt": "Matte Black" }
    ]
  }'`;

  const csvFormat = `title,sku,price,compare_at_price,inventory_quantity,vendor,product_type,tags,image_url
"Premium Wireless Headphones","ATH-WH-001-BLK",89.99,119.99,142,"AudioTech","Electronics","wireless,bluetooth","https://example.com/img1.jpg"
"Ergonomic Keyboard","KBD-ERG-002",149.99,179.99,56,"TypeCraft","Peripherals","keyboard,ergonomic","https://example.com/img2.jpg"
"USB-C Hub 7-in-1","HUB-7IN1-003",39.99,49.99,320,"ConnectPro","Accessories","usb-c,hub,adapter","https://example.com/img3.jpg"`;

  const webhookConfigExample = `curl -X POST "https://api.commerceindex.com/v1/webhooks" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "topic": "inventory/low-stock",
    "address": "https://your-server.com/webhooks/ci",
    "format": "json",
    "secret": "your_webhook_secret_here"
  }'`;

  const webhookPayloadExample = `// Commerce Index sends this to your endpoint:
{
  "id": "evt_abc123def456",
  "topic": "inventory/low-stock",
  "created_at": "2025-12-15T10:30:00Z",
  "data": {
    "product_id": "prod_789",
    "variant_id": "var_012",
    "sku": "ATH-WH-001-BLK",
    "current_quantity": 5,
    "reorder_point": 20,
    "predicted_stockout_days": 3
  }
}`;

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30 mb-4">
          Manual Setup
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Non-Shopify Setup Guide
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Import your products via CSV or API. Works with WooCommerce, BigCommerce, Magento, custom platforms,
          headless commerce, and any system that can make HTTP requests.
        </p>
      </div>

      {/* Step 1: Create Account */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Step 1: Create Your Account
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Mail className="w-8 h-8 text-[#3EF1C7] flex-shrink-0" />
              <div>
                <p className="text-[#E2E8F0] mb-3">
                  Sign up at <span className="text-[#3EF1C7] font-semibold">commerceindex.com/signup</span> using one of two methods:
                </p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="p-3 bg-[#1E293B] border border-[#334155] rounded-lg">
                    <h4 className="text-white font-medium mb-1">Magic Link</h4>
                    <p className="text-sm text-[#94A3B8]">Enter your email and click the link we send. No password needed.</p>
                  </div>
                  <div className="p-3 bg-[#1E293B] border border-[#334155] rounded-lg">
                    <h4 className="text-white font-medium mb-1">Google OAuth</h4>
                    <p className="text-sm text-[#94A3B8]">Sign in with your Google Workspace or personal Google account.</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Step 2: Get API Key */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Step 2: Get Your API Key
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Key className="w-8 h-8 text-[#FFD166] flex-shrink-0" />
              <div>
                <p className="text-[#E2E8F0] mb-3">
                  Navigate to <span className="text-white font-medium">Settings &rarr; Developer &rarr; API Keys</span> in your dashboard.
                  Click <strong className="text-[#3EF1C7]">Generate New Key</strong>.
                </p>
                <div className="p-3 bg-[#1E293B] border border-[#334155] rounded-lg mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-[#FFD166]" />
                    <span className="text-[#FFD166] text-sm font-medium">Important</span>
                  </div>
                  <p className="text-sm text-[#94A3B8]">
                    Your API key is shown only once. Store it securely — in environment variables, a secrets manager, or a .env file (never in source code).
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <h3 className="text-lg font-medium text-white mt-6 mb-3">Authentication Headers</h3>
        <div className="relative">
          <button
            onClick={() => copyToClipboard(authHeaderExample)}
            className="absolute top-3 right-3 p-2 rounded-md bg-[#1E293B] hover:bg-[#334155] transition-colors"
            title="Copy to clipboard"
          >
            <Copy className="w-4 h-4 text-[#64748B]" />
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{authHeaderExample}</code>
          </pre>
        </div>
      </section>

      {/* Step 3: Import Products */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Step 3: Import Products
        </h2>
        <p className="text-[#94A3B8] mb-6">
          You can import products two ways: upload a CSV file through the dashboard, or use the REST API for programmatic access.
        </p>

        {/* CSV Format */}
        <h3 className="text-lg font-medium text-white mb-3">
          <FileSpreadsheet className="w-5 h-5 inline mr-2 text-[#3EF1C7]" />
          Option A: CSV Import
        </h3>
        <p className="text-[#94A3B8] mb-3">
          Upload a CSV file from <span className="text-white font-medium">Products &rarr; Import &rarr; Upload CSV</span>. Use this format:
        </p>
        <div className="relative mb-8">
          <button
            onClick={() => copyToClipboard(csvFormat)}
            className="absolute top-3 right-3 p-2 rounded-md bg-[#1E293B] hover:bg-[#334155] transition-colors"
            title="Copy to clipboard"
          >
            <Copy className="w-4 h-4 text-[#64748B]" />
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{csvFormat}</code>
          </pre>
        </div>

        {/* API Import */}
        <h3 className="text-lg font-medium text-white mb-3">
          <Code2 className="w-5 h-5 inline mr-2 text-[#7A5CFF]" />
          Option B: API Import
        </h3>
        <p className="text-[#94A3B8] mb-3">
          Create products programmatically via a POST request to the products endpoint:
        </p>
        <div className="relative">
          <button
            onClick={() => copyToClipboard(productCreateExample)}
            className="absolute top-3 right-3 p-2 rounded-md bg-[#1E293B] hover:bg-[#334155] transition-colors"
            title="Copy to clipboard"
          >
            <Copy className="w-4 h-4 text-[#64748B]" />
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{productCreateExample}</code>
          </pre>
        </div>
      </section>

      {/* Step 4: Inventory Tracking */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Step 4: Set Up Inventory Tracking
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <p className="text-[#E2E8F0] mb-3">
              If you included <code className="text-[#3EF1C7] bg-[#0B1120] px-2 py-0.5 rounded">inventory_quantity</code> in
              your CSV or API import, inventory tracking is already enabled.
            </p>
            <p className="text-[#94A3B8] mb-3">
              For ongoing inventory updates, you can either push changes via the API or configure your platform to send
              webhook notifications when stock levels change.
            </p>
            <p className="text-[#94A3B8] text-sm">
              The <span className="text-white font-medium">Inventory Agent</span> will begin analyzing your stock levels
              and predicting stockout dates once it has at least 7 days of data.
            </p>
          </CardContent>
        </Card>
      </section>

      {/* Step 5: Configure Webhooks */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Step 5: Configure Webhooks
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Register webhook endpoints to receive real-time notifications from Commerce Index — low stock alerts,
          agent decisions, deal performance updates, and more.
        </p>

        <h3 className="text-lg font-medium text-white mb-3">Register a Webhook</h3>
        <div className="relative mb-6">
          <button
            onClick={() => copyToClipboard(webhookConfigExample)}
            className="absolute top-3 right-3 p-2 rounded-md bg-[#1E293B] hover:bg-[#334155] transition-colors"
            title="Copy to clipboard"
          >
            <Copy className="w-4 h-4 text-[#64748B]" />
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{webhookConfigExample}</code>
          </pre>
        </div>

        <h3 className="text-lg font-medium text-white mb-3">Webhook Payload Example</h3>
        <div className="relative">
          <button
            onClick={() => copyToClipboard(webhookPayloadExample)}
            className="absolute top-3 right-3 p-2 rounded-md bg-[#1E293B] hover:bg-[#334155] transition-colors"
            title="Copy to clipboard"
          >
            <Copy className="w-4 h-4 text-[#64748B]" />
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{webhookPayloadExample}</code>
          </pre>
        </div>
      </section>

      {/* Step 6: Enable Agents */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Step 6: Enable Agents
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Bot className="w-8 h-8 text-[#3EF1C7] flex-shrink-0" />
              <div>
                <p className="text-[#E2E8F0] mb-3">
                  Navigate to <span className="text-white font-medium">Agents</span> in your dashboard and activate the agents
                  you need. All agents start in observation mode — they analyze your data and make recommendations but wait
                  for your approval before taking action.
                </p>
                <p className="text-[#94A3B8] text-sm">
                  We recommend starting with at least 3 agents: Pricing, Inventory, and Deal. You can enable more as you
                  get comfortable with the platform.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* What's Next */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          What's Next?
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <ul className="space-y-3">
              {[
                { text: 'Explore all 80+ AI agents', link: '/docs/agents' },
                { text: 'Set up OpenClaw messaging', link: '/docs/openclaw' },
                { text: 'Follow the Onboarding Checklist', link: '/docs/onboarding-checklist' },
                { text: 'REST API Reference', link: '/docs/rest-api' },
                { text: 'Commerce Health Score', link: '/docs/ci-score' },
              ].map((item, i) => (
                <li key={i}>
                  <Link to={item.link} className="flex items-center gap-2 text-[#3EF1C7] hover:underline">
                    <ChevronRight className="w-4 h-4" />
                    {item.text}
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
